import { toast } from "sonner";
import axiosInstance from "../../utility/axios";
import { getErrorMessage } from "../apiError";
import { AppDispatch } from "../../store/store";
import {
  clearUser,
  createUser,
  deleteUser,
  disableUsersLoading,
  editUser,
  setLoadingUserId,
  setUser,
  setUsers,
  setUsersLoading,
  updateUserStatus,
  setTotalUser,
} from "../../store/slices/users";
import {
  IUserCount,
  IUserCreate,
  IUserMutationResponse,
  IUserStatusResponse,
  IUserUpdate,
  IUsersResponse,
} from "../../store/types/users";

const userUrl = "/api/v1/admin";

export const get_users = async (
  dispatch: AppDispatch,
  offset = 0,
  limit = 10,
): Promise<void> => {
  dispatch(setUsersLoading());
  try {
    const response = await axiosInstance.get<IUsersResponse>(
      `${userUrl}/users`,
      { params: { offset, limit } },
    );
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to load users");
    }
    dispatch(setUsers(response.data));
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to load users"));
  } finally {
    dispatch(disableUsersLoading());
  }
};

export const get_user = async (
  dispatch: AppDispatch,
  id: number,
): Promise<boolean> => {
  dispatch(setUsersLoading());
  try {
    const response = await axiosInstance.get<IUserMutationResponse>(
      `${userUrl}/users/${id}`,
    );
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to load user");
    }
    dispatch(setUser(response.data.data));
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to load user details"));
    return false;
  } finally {
    dispatch(disableUsersLoading());
  }
};

export const clear_user = (dispatch: AppDispatch) => {
  dispatch(clearUser());
};

export const create_user = async (
  dispatch: AppDispatch,
  userData: IUserCreate,
): Promise<boolean> => {
  dispatch(setUsersLoading());
  try {
    const response = await axiosInstance.post<IUserMutationResponse>(
      `${userUrl}/users`,
      userData,
    );
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to create user");
    }
    dispatch(createUser(response.data.data));
    toast.success(response.data.message || "User created successfully");
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to create user"));
    return false;
  } finally {
    dispatch(disableUsersLoading());
  }
};

export const edit_user = async (
  dispatch: AppDispatch,
  id: number,
  userData: IUserUpdate,
): Promise<boolean> => {
  dispatch(setUsersLoading());
  try {
    const response = await axiosInstance.put<IUserMutationResponse>(
      `${userUrl}/users/${id}`,
      userData,
    );
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to update user");
    }
    dispatch(editUser(response.data.data));
    toast.success(response.data.message || "User updated successfully");
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to update user"));
    return false;
  } finally {
    dispatch(disableUsersLoading());
  }
};

export const delete_user = async (
  dispatch: AppDispatch,
  id: number,
): Promise<boolean> => {
  dispatch(setLoadingUserId(id));
  try {
    const response = await axiosInstance.delete<{
      success: boolean;
      message: string;
    }>(`${userUrl}/users/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to delete user");
    }
    dispatch(deleteUser(id));
    toast.success(response.data.message || "User deleted successfully");
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to delete user"));
    return false;
  } finally {
    dispatch(setLoadingUserId(null));
  }
};

export const toggle_user_status = async (
  dispatch: AppDispatch,
  id: number,
  isActive: boolean,
): Promise<boolean> => {
  dispatch(setLoadingUserId(id));
  try {
    const response = await axiosInstance.patch<IUserStatusResponse>(
      `${userUrl}/users/${id}/status`,
      { is_active: isActive },
    );
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to update user status");
    }
    dispatch(updateUserStatus(response.data.data));
    toast.success(response.data.message || "User status updated");
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to update user status"));
    return false;
  } finally {
    dispatch(setLoadingUserId(null));
  }
};

export const get_total_user = async (dispatch: AppDispatch): Promise<void> => {
  dispatch(setUsersLoading());
  try {
    const response = await axiosInstance.get<IUserCount>(
      `${userUrl}/users/analytics`,
    );
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to update user status");
    }
    dispatch(setTotalUser(response.data.data));
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to load training total"));
  } finally {
    dispatch(disableUsersLoading());
  }
};
